/**
 * pages/LandingPage.jsx
 * Creative Dark Homepage — full rewrite.
 * Uses CSS classes from ../styles/landing.css.
 * Features: SVG particle network, typewriter headline, count-up stats,
 * scroll-reveal sections, mouse parallax on hero, plasma CTA pulse.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import '../styles/landing.css';
import EnquiryForm from '../components/EnquiryForm';
import BookMeetingModal from '../components/BookMeetingModal';
import toolCaliper from '../assets/tool_caliper.png';
import partRender from '../assets/part_render.png';

/* ─────────────────────────────────────────────────────────────────── */
/*  SVG Icons                                                           */
/* ─────────────────────────────────────────────────────────────── */
function IconLogo() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
    </svg>
  );
}
function IconUpload() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
    </svg>
  );
}
function IconScan() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );
}
function IconPlan() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}
function IconApprove() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  );
}
function IconDimension() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
    </svg>
  );
}
function IconCC() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  );
}
function IconTolerance() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
      <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
      <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/>
      <line x1="17" y1="16" x2="23" y2="16"/>
    </svg>
  );
}
function IconBatch() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}
function IconLifecycle() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );
}
function IconArrow() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}
function IconChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Custom hooks                                                         */
/* ─────────────────────────────────────────────────────────────────── */
function useCountUp(target, duration = 1400, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    const isFloat = String(target).includes('.');
    const num = parseFloat(target);
    if (isNaN(num)) { setCount(target); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const pct = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - pct, 3);
      const val = num * eased;
      setCount(isFloat ? val.toFixed(1) : Math.round(val));
      if (pct < 1) requestAnimationFrame(step);
      else setCount(isFloat ? num.toFixed(1) : num);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return count;
}

function useScrollReveal(rootMargin = '0px 0px -80px 0px') {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const targets = el.querySelectorAll('.lp-reveal');
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('lp-visible');
      }),
      { rootMargin, threshold: 0.05 }
    );
    targets.forEach(t => obs.observe(t));
    return () => obs.disconnect();
  }, [rootMargin]);
  return ref;
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Data                                                                */
/* ─────────────────────────────────────────────────────────────────── */
const STEPS = [
  { num: '01', icon: <IconUpload />,  title: 'Upload Drawing',
    desc: 'Drop your engineering PDF or image. Supports single files and batch uploads of up to 10 drawings.' },
  { num: '02', icon: <IconScan />,    title: 'AI Scans & Extracts',
    desc: 'The AI pipeline identifies dimensions, tolerances, GD&T callouts, and critical characteristic (CC) symbols.' },
  { num: '03', icon: <IconPlan />,    title: 'Inspection Plan Generated',
    desc: 'A structured, editable inspection plan is produced automatically — ready for export as Excel or PDF.' },
  { num: '04', icon: <IconApprove />, title: 'Review & Approve',
    desc: 'Share the plan with one or more reviewers. Track per-file approval status with a full lifecycle audit trail.' },
];

const FEATURES = [
  { icon: <IconDimension />, title: 'Precision Dimension Extraction',
    desc: 'Detects all linear and angular dimensions, datum references, and GD&T feature control frames directly from the drawing geometry.',
    accent: '#F97316' },
  { icon: <IconCC />,        title: 'CC Symbol Recognition',
    desc: 'Automatically identifies and flags Critical Characteristics from engineering drawings — no manual marking required.',
    accent: '#EF4444' },
  { icon: <IconTolerance />, title: 'Tolerance Analysis',
    desc: 'Captures tol(+) and tol(−) values for every dimension and includes them in exported Excel and PDF inspection plans.',
    accent: '#8B5CF6' },
  { icon: <IconBatch />,     title: 'Batch Processing',
    desc: 'Process up to 10 drawings simultaneously. All files in a batch are grouped and can be shared with reviewers in one action.',
    accent: '#0EA5E9' },
  { icon: <IconApprove />,   title: 'Multi-Reviewer Approval',
    desc: 'Send inspection plans to multiple reviewers at once. Each reviewer gets a unique link and can approve or reject individually.',
    accent: '#10B981' },
  { icon: <IconLifecycle />, title: 'Full Lifecycle Tracking',
    desc: 'Every event — upload, scan, edit, share, and approval — is recorded with timestamps, actor names, and audit-ready history.',
    accent: '#D97706' },
];

const RAW_STATS = [
  { raw: '10', suffix: 'x', label: 'Faster than manual extraction' },
  { raw: '94', suffix: '%', label: 'Dimension capture accuracy' },
  { raw: '10', suffix: '',  label: 'Files processed in parallel' },
  { raw: '100',suffix: '%', label: 'Audit-ready lifecycle trail' },
];

/* ─────────────────────────────────────────────────────────────────── */
/*  StatsBar                                                             */
/* ─────────────────────────────────────────────────────────────────── */
function StatItem({ raw, suffix, label, active }) {
  const count = useCountUp(Number(raw), 1200, active);
  return (
    <div className="lp-stat-item lp-reveal">
      <div className="lp-stat-value">{active ? count : raw}{suffix}</div>
      <div className="lp-stat-label">{label}</div>
    </div>
  );
}

function StatsBar() {
  const ref  = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setActive(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div className="lp-stats" ref={ref}>
      {RAW_STATS.map(s => (
        <StatItem key={s.label} raw={s.raw} suffix={s.suffix} label={s.label} active={active} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/*  HeroV3 — pixel-per-spec implementation of design_handoff v3         */
/*  (nav + hero only; everything below stays the original page)         */
/* ─────────────────────────────────────────────────────────────────── */
const V3 = {
  org: '#FF7A00', orgHover: '#e66e00', orgLight: '#ffa14d',
  bg: '#05070B', body: '#A5B4C7', navText: '#c6cdd6', muted: '#8b98a9',
};

function V3Css() {
  return (
    <style>{`
      @keyframes lp3FadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
      @keyframes lp3PopIn{from{opacity:0;transform:scale(.4)}to{opacity:1;transform:scale(1)}}
      @keyframes lp3Draw{to{stroke-dashoffset:0}}
      @keyframes lp3RingPulse{0%{r:14px;opacity:.9}100%{r:26px;opacity:0}}
      .lp3-fadeup{animation:lp3FadeUp .6s ease both}
      .lp3-fadeup-d{animation:lp3FadeUp .6s ease .15s both}
      .lp3-draw{stroke-dasharray:700;stroke-dashoffset:700;animation:lp3Draw 2.2s ease .5s both}
      .lp3-pop{animation:lp3PopIn .3s ease both}
      .lp3-ring{animation:lp3RingPulse 2.2s ease-out infinite}
      .lp3-link{color:#c6cdd6;background:none;border:none;cursor:pointer;font:500 14.5px Inter,sans-serif;white-space:nowrap;padding:0}
      .lp3-link:hover{color:#fff}
      .lp3-btn-ghost{white-space:nowrap;font:500 14.5px Inter,sans-serif;color:#fff;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.15);border-radius:9px;padding:10px 20px;cursor:pointer;transition:background .2s}
      .lp3-btn-ghost:hover{background:rgba(255,255,255,.09)}
      .lp3-btn-primary{white-space:nowrap;display:inline-flex;align-items:center;gap:8px;font:600 14.5px Inter,sans-serif;color:#fff;background:#FF7A00;border:1px solid #FF7A00;border-radius:9px;padding:10px 20px;cursor:pointer;transition:background .2s,transform .2s}
      .lp3-btn-primary:hover{background:#e66e00}
      .lp3-cta-primary{white-space:nowrap;display:inline-flex;align-items:center;gap:9px;font:600 15.5px Inter,sans-serif;color:#fff;background:#FF7A00;border:1px solid #FF7A00;border-radius:10px;padding:13px 26px;cursor:pointer;box-shadow:0 8px 28px rgba(255,122,0,.25);transition:background .2s,transform .2s}
      .lp3-cta-primary:hover{background:#e66e00;transform:translateY(-2px)}
      .lp3-cta-ghost{white-space:nowrap;display:inline-flex;align-items:center;gap:9px;font:500 15.5px Inter,sans-serif;color:#fff;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.16);border-radius:10px;padding:13px 26px;cursor:pointer;transition:background .2s,transform .2s}
      .lp3-cta-ghost:hover{background:rgba(255,255,255,.09);transform:translateY(-2px)}
      @keyframes lp3WinIn{from{opacity:0;transform:translateY(46px) scale(.94)}to{opacity:1;transform:none}}
      .lp3-win-in{animation:lp3WinIn .95s cubic-bezier(.16,1,.3,1) .15s both}
      .lp3-bar>*{opacity:0;animation:lp3FadeUp .5s ease both}
      .lp3-bar>*:nth-child(1){animation-delay:.55s}
      .lp3-bar>*:nth-child(2){animation-delay:.68s}
      .lp3-bar>*:nth-child(3){animation-delay:.82s}
      .lp3-bar>*:nth-child(4){animation-delay:.96s}
      .lp3-draw-part{stroke-dasharray:1500;stroke-dashoffset:1500;animation:lp3Draw 2.1s cubic-bezier(.16,1,.3,1) .4s both}
      @keyframes lp3IsoFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
      @keyframes lp3CardIn{from{opacity:0;transform:translateX(44px) translateY(12px) scale(.95)}to{opacity:1;transform:none}}
      .lp3-plan-card{animation:lp3CardIn .85s cubic-bezier(.16,1,.3,1) 1.2s both}
      @keyframes lp3PlanFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-9px)}}
      .lp3-plan-float{animation:lp3PlanFloat 6.5s ease-in-out 2.4s infinite}
      .lp3-plan-row{opacity:0;animation:lp3FadeUp .5s cubic-bezier(.16,1,.3,1) both}
      .lp3-plan-btn{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;margin-top:13px;cursor:pointer;font:600 12.5px Inter,sans-serif;color:#fff;background:linear-gradient(135deg,#FF7A00,#e66e00);border:none;border-radius:10px;padding:11px 0;box-shadow:0 6px 20px rgba(255,122,0,.4);transition:transform .3s cubic-bezier(.16,1,.3,1),box-shadow .3s}
      .lp3-plan-btn:hover{transform:translateY(-2px);box-shadow:0 10px 28px rgba(255,122,0,.55)}
      .lp3-plan-btn:active{transform:scale(.97)}
      .lp3-iso{animation:lp3IsoFloat 6s ease-in-out infinite}
      .lp3-iso-b{animation-delay:3s}
      .lp3-hovdim text{cursor:default;transition:fill .2s ease}
      .lp3-hovdim text:hover{fill:#F97316}
      .lp3-pop circle{transition:fill .25s ease,stroke-width .25s ease}
      .lp3-pop:hover circle{fill:#FFE3CB;stroke-width:2.2}
      @media(prefers-reduced-motion:reduce){
        .lp3-win-in,.lp3-bar>*,.lp3-draw-part,.lp3-fadeup,.lp3-fadeup-d,.lp3-draw,.lp3-pop,.lp3-iso{animation:none;opacity:1}
        .lp3-draw-part,.lp3-draw{stroke-dashoffset:0}
      }
      .lp3-approve{transition:background .35s ease,border-color .35s ease,color .35s ease,box-shadow .35s ease,transform .2s ease}
      .lp3-approve:hover{transform:translateY(-1px)}
      .lp3-approve:active{transform:scale(.95)}
      @keyframes lp3StatusPulse{0%{box-shadow:0 0 0 0 currentColor}70%{box-shadow:0 0 0 7px transparent}100%{box-shadow:0 0 0 0 transparent}}
      .lp3-approvedot{animation:lp3StatusPulse 2s ease-out infinite}
      .lp3-view{opacity:0;transform-box:fill-box;transform-origin:center;transform:translateX(14px) scale(.985);transition:opacity .9s cubic-bezier(.16,1,.3,1),transform .9s cubic-bezier(.16,1,.3,1);pointer-events:none}
      .lp3-view-on{opacity:1;transform:none;pointer-events:auto}
      .lp3-layer{transition:opacity .5s cubic-bezier(.16,1,.3,1)}
      .lp3-layer-off{opacity:0;pointer-events:none}
      .lp3-togglerow{cursor:pointer;border-radius:6px;transition:background .25s ease,color .25s ease}
      .lp3-togglerow:hover{background:rgba(255,255,255,.05)}
      .lp3-togglerow:active{transform:scale(.98)}
      .lp3-viewtab{cursor:pointer;transition:border-color .25s ease,color .25s ease,background .25s ease,transform .2s ease}
      .lp3-viewtab:hover{color:#c6cdd6}
      .lp3-viewtab:active{transform:scale(.96)}
      .lp3-paneltab{cursor:pointer;transition:color .25s ease,border-color .25s ease}
      .lp3-tiltwrap{perspective:1700px}
      .lp3-tilt{transform:rotateY(-8deg) rotateX(2deg);transform-style:preserve-3d;transition:transform .55s cubic-bezier(.2,.7,.3,1)}
      .lp3-tiltwrap:hover .lp3-tilt{transform:rotateY(-2.5deg) rotateX(.5deg)}
      @media(max-width:1080px){.lp3-tilt{transform:none}}
    `}</style>
  );
}

function V3ArrowSvg() {
  return (
    <svg width="14" height="11" viewBox="0 0 15 12" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M0 6h13M9 1l5 5-5 5"/>
    </svg>
  );
}

function V3Nav({ onGetStarted, scrollTo, onBookMeeting }) {
  return (
    <nav style={{ display: 'flex', alignItems: 'center', gap: 26,
      padding: '14px clamp(20px,3.5vw,48px)', background: V3.bg,
      borderBottom: '1px solid rgba(255,255,255,.06)',
      position: 'sticky', top: 0, zIndex: 100 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 11, marginRight: 'auto',
        fontWeight: 700, fontSize: 19, letterSpacing: '-.01em', color: '#fff' }}>
        <span style={{ width: 38, height: 38, border: '1.5px solid #FF7A00', borderRadius: 10,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,122,0,.07)', flex: 'none' }}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="#FF7A00" strokeWidth="1.7">
            <rect x="2.5" y="2.5" width="15" height="15" rx="2.5"/>
            <line x1="8" y1="2.5" x2="8" y2="17.5"/><line x1="8" y1="10" x2="17.5" y2="10"/>
          </svg>
        </span>
        <span>Inspectly<span style={{ color: V3.org }}>.AI</span></span>
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <button className="lp3-link" onClick={() => scrollTo('features')}>Features</button>
        <button className="lp3-link" onClick={() => scrollTo('how-it-works')}>How It Works</button>
        <button className="lp3-link" onClick={() => scrollTo('product-preview')}>Product</button>
        <button className="lp3-link" onClick={() => scrollTo('contact')}>Contact</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="lp3-btn-ghost" onClick={() => onGetStarted('uploader')}>Sign In</button>
        <button className="lp3-btn-primary" onClick={() => onBookMeeting('inspectly_nav')}>
          Book Meeting <V3ArrowSvg />
        </button>
      </div>
    </nav>
  );
}

function V3SidebarItem({ active, label, children }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 9px', borderRadius: 7,
      background: active ? 'rgba(255,122,0,.14)' : 'transparent',
      color: active ? V3.orgLight : V3.muted,
      fontWeight: active ? 600 : 400, fontSize: 11.5 }}>
      {children}{label}
    </span>
  );
}

function V3Toggle({ on = true, color = '#FF7A00' }) {
  return (
    <span style={{ marginLeft: 'auto', width: 26, height: 14, borderRadius: 999, background: color, position: 'relative' }}>
      <span style={{ position: 'absolute', right: on ? 2 : 14, top: 2, width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
    </span>
  );
}

function V3AppWindow({ approved, setApproved }) {
  /* Dark CAD-style window — interactive: Layers Detected toggles really
     show/hide layers on the drawing; view tabs + panel tabs are clickable.
     Approval state is lifted to HeroV3 so the chip and the floating card stay in sync. */
  const i13 = { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8 };
  const [layers, setLayers] = React.useState({ dimensions: true, gdt: true, notes: true, grid: true });
  const [view, setView] = React.useState(0);
  const [tab, setTab] = React.useState('summary');
  const flip = (k) => setLayers(p => ({ ...p, [k]: !p[k] }));
  const layerCls = (on) => `lp3-layer${on ? '' : ' lp3-layer-off'}`;
  const viewCls = (i) => `lp3-view${view === i ? ' lp3-view-on' : ''}`;

  /* Auto-tour: cycle through the views (5s each), end back on 3D, then stay
     manual. Any tab click cancels the tour immediately. Skipped entirely for
     users who prefer reduced motion. */
  const [autoTour, setAutoTour] = React.useState(true);
  React.useEffect(() => {
    if (!autoTour) return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setAutoTour(false);
      return undefined;
    }
    const order = [1, 2, 3, 4, 0];
    let i = 0;
    const id = setInterval(() => {
      setView(order[i]);
      i += 1;
      if (i >= order.length) { clearInterval(id); setAutoTour(false); }
    }, 3000);
    return () => clearInterval(id);
  }, [autoTour]);
  const pickView = (i) => { setAutoTour(false); setView(i); };

  const LAYER_ROWS = [
    ['dimensions', 'Dimensions',   '#3fd68c', '#FF7A00'],
    ['gdt',        'GD&T Symbols', '#3fd68c', '#3fd68c'],
    ['notes',      'Notes',        '#3fd68c', '#3fd68c'],
    ['grid',       'Datums',       '#8f7bf5', '#4a7cf0'],
  ];
  const VIEWS = [
    ['3D View',     <svg key="v0" width="30" height="22" viewBox="0 0 48 36" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 3px' }}><path d="M24 5 L40 13 L24 21 L8 13 Z"/><path d="M8 13v6l16 8 16-8v-6"/><ellipse cx="24" cy="13" rx="7" ry="3.4"/></svg>],
    ['Front View',  <svg key="v1" width="30" height="22" viewBox="0 0 48 36" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 3px' }}><rect x="10" y="8" width="28" height="20"/><circle cx="24" cy="18" r="7"/></svg>],
    ['Top View',    <svg key="v2" width="30" height="22" viewBox="0 0 48 36" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 3px' }}><circle cx="24" cy="18" r="12"/><circle cx="24" cy="18" r="5"/><circle cx="24" cy="8.5" r="1.6"/><circle cx="33" cy="23" r="1.6"/><circle cx="15" cy="23" r="1.6"/></svg>],
    ['Right View',  <svg key="v3" width="30" height="22" viewBox="0 0 48 36" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 3px' }}><path d="M14 8h14v6h6v14H14z"/><line x1="14" y1="20" x2="34" y2="20"/></svg>],
    ['Section A-A', <svg key="v4" width="30" height="22" viewBox="0 0 48 36" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 3px' }}><rect x="10" y="8" width="28" height="20"/><path d="M12 26L22 10M18 26L28 10M24 26L34 10" strokeWidth="0.8"/></svg>],
  ];

  return (
    <div className="lp3-win-in" style={{ background: '#0d1119', border: '1px solid rgba(255,255,255,.1)', borderRadius: 12,
      boxShadow: '0 30px 80px rgba(0,0,0,.6)', overflow: 'hidden', fontSize: 12 }}>

      {/* title bar */}
      <div className="lp3-bar" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
        background: '#0a0e15', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <span style={{ display: 'inline-flex', gap: 6, flex: 'none' }}>
          {['#FF5F57', '#FEBC2E', '#28C840'].map(c => (
            <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
          ))}
        </span>
        <span style={{ fontWeight: 600, color: '#dfe4ea', fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Bracket Mounting Flange.pdf
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#3fd68c', fontSize: 11.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M4 12.5l5 5L20 6.5"/></svg>
          Analysis Complete
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid rgba(255,255,255,.14)', borderRadius: 6, padding: '4px 10px', color: '#c6cdd6', fontSize: 11.5 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 15V3M7 8l5-5 5 5M4 21h16v-6"/></svg>Share
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid rgba(255,255,255,.14)', borderRadius: 6, padding: '4px 10px', color: '#c6cdd6', fontSize: 11.5 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3v12M7 10l5 5 5-5M4 21h16"/></svg>Export
          </span>
          <span style={{ width: 26, height: 26, borderRadius: '50%', border: '1px solid rgba(255,255,255,.25)',
            background: 'linear-gradient(135deg,#3b4a63,#232e42)', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flex: 'none' }}>
            <svg width="26" height="26" viewBox="0 0 24 24">
              <circle cx="12" cy="8.6" r="4" fill="#e8c39a"/>
              <path d="M12 4.4c-2.3 0-4 1.8-4 4.2 0 .5.1 1 .3 1.4.2-1.9 1.3-2.6 3.7-2.6s3.5.7 3.7 2.6c.2-.4.3-.9.3-1.4 0-2.4-1.7-4.2-4-4.2z" fill="#4a3524"/>
              <path d="M3.5 22c1.3-4.2 4.6-6.1 8.5-6.1s7.2 1.9 8.5 6.1z" fill="#5f7291"/>
            </svg>
          </span>
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 500 }}>
        {/* left sidebar */}
        <div style={{ width: 112, flex: 'none', background: '#0a0e15', borderRight: '1px solid rgba(255,255,255,.06)',
          padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <V3SidebarItem active label="Upload"><svg {...i13}><path d="M12 15V3M7 8l5-5 5 5M4 21h16v-6"/></svg></V3SidebarItem>
          <V3SidebarItem label="Analyze"><svg {...i13}><path d="M2 12h4l3-8 4 16 3-8h6"/></svg></V3SidebarItem>
          <V3SidebarItem label="Features"><svg {...i13}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg></V3SidebarItem>
          <V3SidebarItem label="Inspection Plan"><svg {...i13}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h5"/></svg></V3SidebarItem>
          <V3SidebarItem label="Reports"><svg {...i13}><path d="M3 3v18h18"/><path d="M7 14v4M12 10v8M17 6v12"/></svg></V3SidebarItem>
          <V3SidebarItem label="History"><svg {...i13}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/></svg></V3SidebarItem>
          <V3SidebarItem label="Settings"><svg {...i13}><circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2L14 3h-4l-.5 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2L10 21h4l.5-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z"/></svg></V3SidebarItem>
        </div>

        {/* canvas + view strip */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, position: 'relative',
            background: 'radial-gradient(600px 300px at 50% 30%,rgba(80,110,150,.06),transparent)' }}>
            {/* blueprint grid — always visible */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
              backgroundImage: 'linear-gradient(rgba(140,165,195,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(140,165,195,.05) 1px,transparent 1px)',
              backgroundSize: '30px 30px' }} />
            <svg viewBox="6 12 652 348" preserveAspectRatio="xMidYMid meet"
              style={{ display: 'block', width: '100%', height: '100%', position: 'absolute', inset: 0 }}
              role="img" aria-label="Ballooned engineering drawing of a bracket mounting flange with dimensions and GD&T">
              <defs>
                <pattern id="lp3HatchC" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(170,195,225,.4)" strokeWidth="0.9"/>
                </pattern>
              </defs>
              {/* ═══ VIEW 0 — bearing bracket + 3D renders ═══ */}
              <g className={viewCls(0)}>
                {/* 3D CAD renders (transparent PNG) */}
                <image href={partRender} x="4" y="46" width="148" height="272"
                  preserveAspectRatio="xMidYMid meet" style={{ filter: 'brightness(1.35) contrast(1.05)' }}/>
                <g transform="translate(100,-4) scale(0.85)">
                  {/* third-angle projection symbol */}
                  <g stroke="rgba(159,182,204,.7)" fill="none" strokeWidth="1">
                    <circle cx="152" cy="36" r="8"/><circle cx="152" cy="36" r="3.2"/>
                    <path d="M166 29 l16 3 v8 l-16 3 z"/>
                  </g>
                  <text x="142" y="57" fontFamily="Inter,sans-serif" fontSize="7.5" fill="rgba(159,182,204,.7)">THIRD ANGLE</text>
                  <text x="142" y="66" fontFamily="Inter,sans-serif" fontSize="7.5" fill="rgba(159,182,204,.7)">PROJECTION</text>

                  {/* DATUMS layer: centerlines + datum flag */}
                  <g className={layerCls(layers.grid)}>
                    <g stroke="rgba(150,175,205,.45)" strokeWidth="0.9" strokeDasharray="13 5 3 5">
                      <line x1="310" y1="66" x2="310" y2="316"/>
                      <line x1="146" y1="192" x2="480" y2="192"/>
                    </g>
                    <g stroke="rgba(159,182,204,.7)" strokeWidth="1" fill="none">
                      <path d="M152,296 l-18,14 h-16 v14 h16 z" strokeWidth="0.8"/>
                    </g>
                    <text x="126" y="320" fontFamily="Inter,sans-serif" fontSize="10.5" fontWeight="700" fill="#dbe5f0" textAnchor="middle">B</text>
                  </g>

                  {/* part geometry */}
                  <g className="lp3-draw-part" stroke="rgba(226,236,246,.95)" fill="none" strokeWidth="1.5" strokeLinejoin="round">
                    <path d="M172,272 L172,158 L252,94 L368,94 L448,158 L448,272"/>
                    <path d="M152,272 h316 v30 h-112 v-13 h-92 v13 h-112 z"/>
                    <circle cx="310" cy="190" r="49"/>
                    <circle cx="310" cy="190" r="41" strokeWidth="0.9" opacity="0.7"/>
                    <circle cx="252" cy="140" r="9.5"/><circle cx="368" cy="140" r="9.5"/>
                    <circle cx="268" cy="243" r="8"/><circle cx="352" cy="243" r="8"/>
                    <path d="M196,172 v76 a7 7 0 0 0 14 0 v-76 a7 7 0 0 0 -14 0 z" strokeWidth="1.1"/>
                    <path d="M410,172 v76 a7 7 0 0 0 14 0 v-76 a7 7 0 0 0 -14 0 z" strokeWidth="1.1"/>
                    <path d="M216,276 v18 M226,276 v18 M394,276 v18 M404,276 v18" strokeWidth="1.1"/>
                  </g>

                  {/* section arrows F—F */}
                  <g stroke="#dbe5f0" fill="#dbe5f0" strokeWidth="1.1">
                    <line x1="252" y1="88" x2="252" y2="70" fill="none"/>
                    <path d="M252,66 l-4,9 h8 z"/>
                    <line x1="368" y1="88" x2="368" y2="70" fill="none"/>
                    <path d="M368,66 l-4,9 h8 z"/>
                  </g>
                  <text x="238" y="62" fontFamily="Inter,sans-serif" fontSize="12" fontWeight="700" fill="#dbe5f0">F</text>
                  <text x="376" y="62" fontFamily="Inter,sans-serif" fontSize="12" fontWeight="700" fill="#dbe5f0">F</text>

                  {/* DIMENSIONS layer */}
                  <g className={layerCls(layers.dimensions)}>
                    <g className="lp3-draw" stroke="rgba(186,206,228,.95)" fill="none" strokeWidth="0.9">
                      <line x1="172" y1="152" x2="172" y2="26"/><line x1="448" y1="152" x2="448" y2="26"/>
                      <line x1="178" y1="32" x2="442" y2="32"/>
                      <path d="M172,32 l8,-3 v6 z M448,32 l-8,-3 v6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                      <line x1="252" y1="128" x2="252" y2="52"/><line x1="368" y1="128" x2="368" y2="52"/>
                      <line x1="258" y1="58" x2="362" y2="58"/>
                      <path d="M252,58 l8,-3 v6 z M368,58 l-8,-3 v6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                      <line x1="166" y1="94" x2="120" y2="94"/><line x1="146" y1="272" x2="120" y2="272"/>
                      <line x1="128" y1="100" x2="128" y2="266"/>
                      <path d="M128,94 l-3,8 h6 z M128,272 l-3,-8 h6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                      <line x1="454" y1="192" x2="508" y2="192"/><line x1="454" y1="272" x2="508" y2="272"/>
                      <line x1="500" y1="198" x2="500" y2="266"/>
                      <path d="M500,192 l-3,8 h6 z M500,272 l-3,-8 h6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                      <line x1="152" y1="308" x2="152" y2="338"/><line x1="468" y1="308" x2="468" y2="338"/>
                      <line x1="158" y1="330" x2="462" y2="330"/>
                      <path d="M152,330 l8,-3 v6 z M468,330 l-8,-3 v6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                      <line x1="356" y1="292" x2="356" y2="312"/><line x1="404" y1="296" x2="404" y2="312"/>
                    </g>
                    <g className="lp3-hovdim" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="700" fill="#f5f9fe" style={{ fontFeatureSettings: "'tnum' 1" }}>
                      <text x="310" y="24" textAnchor="middle">3.500±.005</text>
                      <text x="310" y="51" textAnchor="middle">2.500±.003</text>
                      <text x="114" y="188" textAnchor="end">2.250±.003</text>
                      <text x="512" y="236" textAnchor="start">1.125±.002</text>
                      <text x="310" y="352" textAnchor="middle">4.000±.005</text>
                      <text x="381" y="322" textAnchor="start" fontSize="9.5">.500±.002</text>
                    </g>
                  </g>

                  {/* GD&T layer */}
                  <g className={layerCls(layers.gdt)}>
                    <g stroke="#FF7A00" fill="none" strokeWidth="1.3">
                      <circle cx="352" cy="243" r="13"/>
                      <circle className="lp3-ring" cx="352" cy="243" r="13" opacity="0.8"/>
                      <path d="M362,252 L420,296 h12" strokeWidth="0.95"/>
                      <rect x="434" y="286" width="21" height="19" fill="rgba(255,122,0,.08)"/>
                      <rect x="455" y="286" width="42" height="19" fill="rgba(255,122,0,.08)"/>
                      <rect x="497" y="286" width="18" height="19" fill="rgba(255,122,0,.08)"/>
                      <rect x="515" y="286" width="18" height="19" fill="rgba(255,122,0,.08)"/>
                      <path d="M382,116 L432,76 h10" strokeWidth="0.95"/>
                      <rect x="444" y="66" width="20" height="18" fill="rgba(255,122,0,.08)"/>
                      <rect x="464" y="66" width="40" height="18" fill="rgba(255,122,0,.08)"/>
                      <rect x="504" y="66" width="17" height="18" fill="rgba(255,122,0,.08)"/>
                    </g>
                    <g fontFamily="Inter, sans-serif" fontSize="10.5" fontWeight="600" fill="#ffa14d" style={{ fontFeatureSettings: "'tnum' 1" }}>
                      <text x="444" y="299" textAnchor="middle">⌖</text>
                      <text x="476" y="299" textAnchor="middle">.006</text>
                      <text x="506" y="299" textAnchor="middle">A</text>
                      <text x="524" y="299" textAnchor="middle">B</text>
                      <text x="444" y="311" textAnchor="start" fontSize="8.5">2X</text>
                      <text x="454" y="79" textAnchor="middle">∥</text>
                      <text x="484" y="79" textAnchor="middle">.010</text>
                      <text x="512" y="79" textAnchor="middle">A</text>
                    </g>
                  </g>

                  {/* CC balloons — always visible */}
                  <g fontFamily="Inter, sans-serif" fontSize="10.5" fontWeight="700">
                    {[
                      { n: 1, x: 404, y: 22,  d: '.8s'  },
                      { n: 2, x: 96,  y: 118, d: '1s'   },
                      { n: 3, x: 540, y: 176, d: '1.2s' },
                      { n: 4, x: 244, y: 344, d: '1.4s' },
                      { n: 5, x: 560, y: 116, d: '1.6s' },
                    ].map(b => (
                      <g key={b.n} className="lp3-pop" style={{ animationDelay: b.d }}>
                        <circle cx={b.x} cy={b.y} r="10.5" fill="#0d1119" stroke="#FF7A00" strokeWidth="1.4"/>
                        <text x={b.x} y={b.y + 3.7} textAnchor="middle" fill="#ffa14d">{b.n}</text>
                      </g>
                    ))}
                  </g>
                </g>
              </g>

              {/* ═══ VIEW 1 — FRONT VIEW (bracket) ═══ */}
              <g className={viewCls(1)}>
                <g transform="translate(122,-4) scale(0.85)">
                  <g className={layerCls(layers.grid)} stroke="rgba(150,175,205,.45)" strokeWidth="0.9" strokeDasharray="13 5 3 5">
                    <line x1="310" y1="66" x2="310" y2="316"/>
                    <line x1="146" y1="192" x2="480" y2="192"/>
                  </g>
                  <g stroke="rgba(226,236,246,.95)" fill="none" strokeWidth="1.5" strokeLinejoin="round">
                    <path d="M172,272 L172,158 L252,94 L368,94 L448,158 L448,272"/>
                    <path d="M152,272 h316 v30 h-112 v-13 h-92 v13 h-112 z"/>
                    <circle cx="310" cy="190" r="49"/>
                    <circle cx="310" cy="190" r="41" strokeWidth="0.9" opacity="0.7"/>
                    <circle cx="252" cy="140" r="9.5"/><circle cx="368" cy="140" r="9.5"/>
                    <circle cx="268" cy="243" r="8"/><circle cx="352" cy="243" r="8"/>
                    <path d="M196,172 v76 a7 7 0 0 0 14 0 v-76 a7 7 0 0 0 -14 0 z" strokeWidth="1.1"/>
                    <path d="M410,172 v76 a7 7 0 0 0 14 0 v-76 a7 7 0 0 0 -14 0 z" strokeWidth="1.1"/>
                    <path d="M216,276 v18 M226,276 v18 M394,276 v18 M404,276 v18" strokeWidth="1.1"/>
                  </g>
                  <g className={layerCls(layers.dimensions)}>
                    <g className="lp3-draw" stroke="rgba(186,206,228,.95)" fill="none" strokeWidth="0.9">
                      <line x1="172" y1="152" x2="172" y2="26"/><line x1="448" y1="152" x2="448" y2="26"/>
                      <line x1="178" y1="32" x2="442" y2="32"/>
                      <path d="M172,32 l8,-3 v6 z M448,32 l-8,-3 v6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                      <line x1="166" y1="94" x2="120" y2="94"/><line x1="146" y1="272" x2="120" y2="272"/>
                      <line x1="128" y1="100" x2="128" y2="266"/>
                      <path d="M128,94 l-3,8 h6 z M128,272 l-3,-8 h6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                      <line x1="454" y1="192" x2="508" y2="192"/><line x1="454" y1="272" x2="508" y2="272"/>
                      <line x1="500" y1="198" x2="500" y2="266"/>
                      <path d="M500,192 l-3,8 h6 z M500,272 l-3,-8 h6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                    </g>
                    <g className="lp3-hovdim" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="700" fill="#f5f9fe" style={{ fontFeatureSettings: "'tnum' 1" }}>
                      <text x="310" y="24" textAnchor="middle">3.500±.005</text>
                      <text x="114" y="188" textAnchor="end">2.250±.003</text>
                      <text x="512" y="236" textAnchor="start">1.125±.002</text>
                    </g>
                  </g>
                  <text x="310" y="345" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="13" fontWeight="700"
                    letterSpacing="3.5" fill="#8b98a9">FRONT VIEW</text>
                </g>
              </g>

              {/* ═══ VIEW 2 — TOP VIEW (bracket plan) ═══ */}
              <g className={viewCls(2)}>
                <g className={layerCls(layers.grid)} stroke="rgba(150,175,205,.45)" strokeWidth="0.8" strokeDasharray="13 5 3 5">
                  <line x1="330" y1="70" x2="330" y2="290"/>
                  <line x1="140" y1="180" x2="520" y2="180"/>
                </g>
                <g stroke="rgba(226,236,246,.95)" fill="none" strokeWidth="1.4" strokeLinejoin="round">
                  <rect x="160" y="120" width="340" height="120" rx="6"/>
                  <path d="M275,240 v-14 h110 v14" strokeWidth="1.1"/>
                  <rect x="190" y="150" width="280" height="44" rx="4" strokeWidth="1.2"/>
                  <rect x="216" y="150" width="14" height="44" strokeWidth="1"/>
                  <rect x="430" y="150" width="14" height="44" strokeWidth="1"/>
                  <circle cx="205" cy="137" r="8"/><circle cx="455" cy="137" r="8"/>
                  <circle cx="205" cy="223" r="8"/><circle cx="455" cy="223" r="8"/>
                </g>
                <g stroke="rgba(170,195,225,.5)" strokeWidth="0.9" strokeDasharray="6 5" fill="none">
                  <line x1="289" y1="150" x2="289" y2="194"/><line x1="371" y1="150" x2="371" y2="194"/>
                </g>
                <g className={layerCls(layers.dimensions)}>
                  <g className="lp3-draw" stroke="rgba(186,206,228,.95)" fill="none" strokeWidth="0.85">
                    <line x1="160" y1="114" x2="160" y2="76"/><line x1="500" y1="114" x2="500" y2="76"/>
                    <line x1="168" y1="83" x2="492" y2="83"/>
                    <path d="M160,83 l9,-3.5 v7 z M500,83 l-9,-3.5 v7 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                    <line x1="506" y1="120" x2="556" y2="120"/><line x1="506" y1="240" x2="556" y2="240"/>
                    <line x1="548" y1="126" x2="548" y2="234"/>
                    <path d="M548,120 l-3.5,9 h7 z M548,240 l-3.5,-9 h7 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                  </g>
                  <g className="lp3-hovdim" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="700" fill="#f5f9fe" style={{ fontFeatureSettings: "'tnum' 1" }}>
                    <text x="330" y="72" textAnchor="middle">4.000±.005</text>
                    <text x="560" y="184" textAnchor="start">2.000±.003</text>
                  </g>
                </g>
                <text x="330" y="322" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="12" fontWeight="700"
                  letterSpacing="3" fill="#8b98a9">TOP VIEW</text>
              </g>

              {/* ═══ VIEW 3 — RIGHT VIEW (bracket side) ═══ */}
              <g className={viewCls(3)}>
                <g className={layerCls(layers.grid)} stroke="rgba(150,175,205,.45)" strokeWidth="0.8" strokeDasharray="13 5 3 5">
                  <line x1="240" y1="172" x2="450" y2="172"/>
                  <line x1="330" y1="70" x2="330" y2="300"/>
                </g>
                <g stroke="rgba(226,236,246,.95)" fill="none" strokeWidth="1.4" strokeLinejoin="round">
                  <rect x="250" y="252" width="160" height="30" rx="3"/>
                  <path d="M296,252 v-152 l8,-10 h28 l8,10 v152"/>
                  <rect x="340" y="140" width="26" height="64" rx="4" strokeWidth="1.2"/>
                  <line x1="296" y1="220" x2="340" y2="220" strokeWidth="0.9" opacity="0.6"/>
                  <path d="M266,282 v14 M278,282 v14 M382,282 v14 M394,282 v14" strokeWidth="1.1"/>
                </g>
                <g stroke="rgba(170,195,225,.5)" strokeWidth="0.9" strokeDasharray="6 5" fill="none">
                  <line x1="290" y1="158" x2="372" y2="158"/><line x1="290" y1="186" x2="372" y2="186"/>
                </g>
                <g className={layerCls(layers.dimensions)}>
                  <g className="lp3-draw" stroke="rgba(186,206,228,.95)" fill="none" strokeWidth="0.85">
                    <line x1="290" y1="90" x2="216" y2="90"/><line x1="244" y1="282" x2="216" y2="282"/>
                    <line x1="224" y1="96" x2="224" y2="276"/>
                    <path d="M224,90 l-3.5,9 h7 z M224,282 l-3.5,-9 h7 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                    <line x1="372" y1="172" x2="452" y2="172"/><line x1="416" y1="282" x2="452" y2="282"/>
                    <line x1="444" y1="178" x2="444" y2="276"/>
                    <path d="M444,172 l-3.5,9 h7 z M444,282 l-3.5,-9 h7 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                  </g>
                  <g className="lp3-hovdim" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="700" fill="#f5f9fe" style={{ fontFeatureSettings: "'tnum' 1" }}>
                    <text x="208" y="190" textAnchor="end">2.250±.003</text>
                    <text x="456" y="232" textAnchor="start">1.125±.002</text>
                  </g>
                </g>
                <text x="330" y="322" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="12" fontWeight="700"
                  letterSpacing="3" fill="#8b98a9">RIGHT VIEW</text>
              </g>

              {/* ═══ VIEW 4 — SECTION A-A (bracket, hatched) ═══ */}
              <g className={viewCls(4)}>
                <g transform="translate(122,-4) scale(0.85)">
                  <g className={layerCls(layers.grid)} stroke="rgba(150,175,205,.45)" strokeWidth="0.9" strokeDasharray="13 5 3 5">
                    <line x1="310" y1="66" x2="310" y2="330"/>
                    <line x1="146" y1="192" x2="480" y2="192"/>
                  </g>
                  {/* hatched cut material */}
                  <g fill="url(#lp3HatchC)" stroke="rgba(226,236,246,.9)" strokeWidth="1.2" strokeLinejoin="round">
                    <path d="M172,272 L172,158 L252,94 L261,98 L261,272 Z"/>
                    <path d="M448,272 L448,158 L368,94 L359,98 L359,272 Z"/>
                    <path d="M152,272 h109 v30 h-109 z"/>
                    <path d="M359,272 h109 v30 h-109 z"/>
                  </g>
                  {/* bore walls + base void */}
                  <g stroke="rgba(226,236,246,.95)" fill="none" strokeWidth="1.4">
                    <line x1="261" y1="98" x2="261" y2="302"/>
                    <line x1="359" y1="98" x2="359" y2="302"/>
                    <line x1="261" y1="302" x2="359" y2="302" strokeWidth="1" opacity="0.6"/>
                  </g>
                  <g className={layerCls(layers.dimensions)}>
                    <g className="lp3-draw" stroke="rgba(186,206,228,.95)" fill="none" strokeWidth="0.9">
                      <path d="M310,200 L420,250 h18"/>
                      <line x1="261" y1="92" x2="261" y2="56"/><line x1="359" y1="92" x2="359" y2="56"/>
                      <line x1="267" y1="63" x2="353" y2="63"/>
                      <path d="M261,63 l8,-3 v6 z M359,63 l-8,-3 v6 z" fill="rgba(186,206,228,.95)" stroke="none"/>
                    </g>
                    <g className="lp3-hovdim" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="700" fill="#f5f9fe" style={{ fontFeatureSettings: "'tnum' 1" }}>
                      <text x="310" y="54" textAnchor="middle">Ø1.250 THRU</text>
                      <text x="442" y="246" textAnchor="start">Ø1.250±.002</text>
                    </g>
                  </g>
                  <text x="310" y="345" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="13" fontWeight="700"
                    letterSpacing="3.5" fill="#8b98a9">SECTION A-A</text>
                </g>
              </g>
            </svg>
            {/* drawing notes — boxed panel, bottom-right corner just above the view tabs (crisp HTML) */}
            <div className={layerCls(layers.notes)} style={{
              position: 'absolute', right: 10, bottom: 8,
              background: 'rgba(9,13,20,.95)', border: '1px solid rgba(159,182,204,.45)',
              borderRadius: 10, padding: '10px 16px 11px',
              fontFamily: 'Inter, sans-serif', lineHeight: 1.75 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#dbe5f0', letterSpacing: '.15em', marginBottom: 2 }}>NOTES:</div>
              <div style={{ fontSize: 12, color: '#c7d4e6', whiteSpace: 'nowrap' }}>1. MATERIAL: AL 6061-T6</div>
              <div style={{ fontSize: 12, color: '#c7d4e6', whiteSpace: 'nowrap' }}>2. THICKNESS: 0.6 MIN</div>
              <div style={{ fontSize: 12, color: '#c7d4e6', whiteSpace: 'nowrap' }}>3. BREAK ALL SHARP EDGES</div>
            </div>
            {/* approval status chip — clickable, green ↔ red, pulsing */}
            <span onClick={() => setApproved(a => !a)} title="Click to toggle approval status"
              className="lp3-approve" style={{
              position: 'absolute', left: 10, top: 8, display: 'inline-flex', alignItems: 'center', gap: 7,
              cursor: 'pointer', userSelect: 'none', borderRadius: 999, padding: '5px 12px',
              fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em',
              background: approved ? 'rgba(63,214,140,.12)' : 'rgba(240,82,82,.12)',
              border: `1px solid ${approved ? 'rgba(63,214,140,.55)' : 'rgba(240,82,82,.55)'}`,
              color: approved ? '#3fd68c' : '#f47a7a',
              boxShadow: approved ? '0 0 12px rgba(63,214,140,.18)' : '0 0 12px rgba(240,82,82,.18)',
              transition: 'background .35s ease, border-color .35s ease, color .35s ease, box-shadow .35s ease',
            }}>
              <span className="lp3-approvedot" style={{ width: 8, height: 8, borderRadius: '50%', flex: 'none',
                background: approved ? '#3fd68c' : '#f05252',
                transition: 'background .35s ease' }} />
              Approval Status · {approved ? 'Approved' : 'Pending'}
            </span>
            <div style={{ position: 'absolute', left: 10, bottom: 8, display: 'inline-flex', gap: 4,
              background: '#0a0e15', border: '1px solid rgba(255,255,255,.09)', borderRadius: 8,
              padding: '4px 6px', color: '#8b98a9', fontSize: 12 }}>
              <span style={{ padding: '2px 6px' }}>⟲</span><span style={{ padding: '2px 6px' }}>⟳</span>
              <span style={{ padding: '2px 6px', borderLeft: '1px solid rgba(255,255,255,.09)' }}>⊕</span>
              <span style={{ padding: '2px 6px' }}>⊖</span>
              <span style={{ padding: '2px 6px', borderLeft: '1px solid rgba(255,255,255,.09)' }}>⤢</span>
              <span style={{ padding: '2px 6px' }}>✕</span>
            </div>
          </div>

          {/* view strip — clickable */}
          <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,.06)', background: '#0b0f16', overflow: 'hidden' }}>
            {VIEWS.map(([label, icon], i) => {
              const active = view === i;
              return (
                <span key={label} className="lp3-viewtab" onClick={() => pickView(i)} style={{
                  flex: 1, minWidth: 0, textAlign: 'center', borderRadius: 8, padding: '8px 4px 6px',
                  border: active ? '1.5px solid #FF7A00' : '1px solid rgba(255,255,255,.1)',
                  background: active ? 'rgba(255,122,0,.08)' : 'transparent',
                  color: active ? '#ffa14d' : '#8b98a9', fontSize: 10, fontWeight: active ? 600 : 500 }}>
                  {icon}{label}
                </span>
              );
            })}
          </div>
        </div>

        {/* right panel */}
        <div style={{ width: 212, flex: 'none', background: '#0b0f17', borderLeft: '1px solid rgba(255,255,255,.06)', display: 'flex', flexDirection: 'column', fontSize: 11.5 }}>
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
            {[['summary', 'Detection Summary'], ['props', 'Properties']].map(([k, label]) => (
              <span key={k} className="lp3-paneltab" onClick={() => setTab(k)} style={{
                flex: 1, textAlign: 'center', padding: '9px 4px', whiteSpace: 'nowrap',
                fontWeight: tab === k ? 600 : 400, color: tab === k ? '#fff' : '#8b98a9',
                borderBottom: tab === k ? '2px solid #FF7A00' : '2px solid transparent' }}>{label}</span>
            ))}
          </div>
          {tab === 'summary' ? (
            <>
              <div style={{ padding: '11px 12px', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                <div style={{ fontWeight: 600, color: '#dfe4ea', marginBottom: 7 }}>Detection Summary</div>
                {[['Total Characteristics', '24'], ['Critical (CC)', '5'], ['Symbols', '\u25B2 Filled Triangle'], ['Review', '12']].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#8b98a9' }}>
                    {k} <span style={{ color: '#fff', fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding: '11px 12px', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                <div style={{ fontWeight: 600, color: '#dfe4ea', marginBottom: 7 }}>Layers Detected</div>
                {LAYER_ROWS.map(([key, label, dot, trackColor]) => {
                  const on = layers[key];
                  return (
                    <div key={key} className="lp3-togglerow" onClick={() => flip(key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 4px', color: on ? '#c6cdd6' : '#5d6773' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: on ? dot : '#3a4149', flex: 'none', transition: 'background .3s' }} />
                      {label}
                      <span style={{ marginLeft: 'auto', width: 27, height: 15, borderRadius: 999, flex: 'none',
                        background: on ? trackColor : '#3a4149', position: 'relative', transition: 'background .35s ease',
                        boxShadow: on ? `0 0 9px ${trackColor}66` : 'none' }}>
                        <span style={{ position: 'absolute', top: 2, left: on ? 14 : 2, width: 11, height: 11,
                          borderRadius: '50%', background: '#fff', transition: 'left .35s cubic-bezier(.16,1,.3,1)' }} />
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: '11px 12px 8px' }}>
                <div style={{ fontWeight: 600, color: '#dfe4ea', marginBottom: 7 }}>Inspection Plan Preview</div>
                <div style={{ display: 'grid', gridTemplateColumns: '14px 1fr 52px 34px', gap: '3px 6px', fontFeatureSettings: "'tnum' 1" }}>
                  {['#', 'Characteristic', 'Type', 'Tol.'].map(h => <span key={h} style={{ color: '#65707e', fontSize: 10 }}>{h}</span>)}
                  {[
                    ['1', 'Ø40.00', 'Diameter', '±0.02'],
                    ['2', '92.00', 'Linear', '±0.05'],
                    ['3', '70.00', 'Linear', '±0.05'],
                    ['4', 'Ø28.00', 'Diameter', '±0.02'],
                  ].map(row => (
                    <React.Fragment key={row[0]}>
                      <span style={{ color: '#8b98a9' }}>{row[0]}</span>
                      <span style={{ color: '#dfe4ea' }}>{row[1]}</span>
                      <span style={{ color: '#8b98a9' }}>{row[2]}</span>
                      <span style={{ color: '#8b98a9' }}>{row[3]}</span>
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ textAlign: 'center', padding: '7px 0 4px' }}>
                  <span style={{ fontSize: 11, color: '#ffa14d' }}>View All (24)</span>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: '11px 12px' }}>
              <div style={{ fontWeight: 600, color: '#dfe4ea', marginBottom: 7 }}>Drawing Properties</div>
              {[
                ['File', 'Bracket_Flange.pdf'], ['Sheets', '2'], ['Units', 'mm'],
                ['Standard', 'ASME Y14.5-2018'], ['Scale', '1 : 1'], ['Revision', 'C'],
                ['Projection', 'Third angle'], ['Material', 'AL 6061-T6'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '4.5px 0', color: '#8b98a9', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
                  {k} <span style={{ color: '#dfe4ea', fontWeight: 600, textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 'auto', padding: '10px 12px 12px' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, padding: '9px 6px',
              color: '#dfe4ea', fontWeight: 600, fontSize: 11.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3fd68c" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 8l8 8M16 8l-8 8"/></svg>
              Export Plan (XLSX)
              <svg width="9" height="6" viewBox="0 0 10 6" fill="none" stroke="#8b98a9" strokeWidth="1.5"><path d="M1 1l4 4 4-4"/></svg>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/*  DashboardMock — replica of the real InSpectly dashboard             */
/* ─────────────────────────────────────────────────────────────────── */
function DashboardMock() {
  const D = { side: '#211A14', main: '#F7F5F1', card: '#fff', border: '#EAE6DF',
    text: '#1F2430', muted: '#8A8F98', org: '#F97316' };
  const i11 = { width: 11, height: 11, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9 };
  const spark = (color, d) => (
    <svg width="48" height="20" viewBox="0 0 48 20" style={{ marginLeft: 'auto' }}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.75"/>
    </svg>
  );
  const statCards = [
    ['TOTAL SCANS', '1115', 'All time', D.org, 'M2,16 C10,4 16,3 24,10 C32,17 40,14 46,15',
      <svg key="i" {...i11}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></svg>],
    ['SUCCESSFUL', '866', '78% success rate', '#16A34A', 'M2,15 C10,7 18,5 26,11 C34,16 42,12 46,13',
      <svg key="i" {...i11}><circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 5-5.5"/></svg>],
    ['FAILED', '190', 'Need attention', '#EF4444', 'M2,14 C10,5 16,6 24,13 C32,18 40,16 46,17',
      <svg key="i" {...i11}><circle cx="12" cy="12" r="9"/><path d="M9 9l6 6M15 9l-6 6"/></svg>],
    ['CC SYMBOLS', '1758', 'Critical found', '#8B5CF6', 'M2,15 C12,9 20,12 28,8 C36,5 42,10 46,9',
      <svg key="i" {...i11}><path d="M12 3l2.6 5.6 6.4.8-4.7 4.3 1.2 6.3-5.5-3.1-5.5 3.1 1.2-6.3L3 9.4l6.4-.8z"/></svg>],
    ['INSPECTION ITEMS', '23302', 'Generated total', '#3B82F6', 'M2,16 C10,10 18,12 26,7 C34,3 42,7 46,5',
      <svg key="i" {...i11}><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>],
  ];
  return (
    <div style={{ maxWidth: 980, margin: '56px auto 0', background: D.card, border: `1px solid ${D.border}`,
      borderRadius: 16, overflow: 'hidden', boxShadow: '0 34px 90px rgba(31,36,48,.18)', fontSize: 11, textAlign: 'left' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
        background: '#FDFCFA', borderBottom: `1px solid ${D.border}` }}>
        {['#FF5F57', '#FEBC2E', '#28C840'].map(c => (
          <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
        ))}
        <span style={{ fontWeight: 700, color: D.text, fontSize: 12.5, marginLeft: 4 }}>InSpectly Dashboard</span>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, color: D.org,
          border: '1px solid rgba(249,115,22,.4)', background: 'rgba(249,115,22,.08)',
          padding: '3px 11px', borderRadius: 999 }}>Live Preview</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {/* sidebar */}
        <div style={{ width: 140, flex: 'none', background: D.side, display: 'flex', flexDirection: 'column', padding: '12px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 11 }}>
            <span style={{ width: 24, height: 24, borderRadius: 7, background: 'rgba(249,115,22,.16)',
              border: '1px solid rgba(249,115,22,.5)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
              <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="#F97316" strokeWidth="1.8">
                <rect x="2.5" y="2.5" width="15" height="15" rx="2.5"/><line x1="8" y1="2.5" x2="8" y2="17.5"/><line x1="8" y1="10" x2="17.5" y2="10"/>
              </svg>
            </span>
            <span style={{ lineHeight: 1.15 }}>
              <span style={{ display: 'block', fontSize: 10.5, fontWeight: 800, color: '#fff' }}>InSpectly.AI</span>
              <span style={{ display: 'block', fontSize: 7, color: 'rgba(255,255,255,.45)' }}>Engineering Intelligence</span>
            </span>
          </div>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: 'linear-gradient(135deg,#F97316,#ea6a0e)', color: '#fff', fontWeight: 700, fontSize: 10,
            borderRadius: 8, padding: '6px 4px', marginBottom: 12, boxShadow: '0 4px 12px rgba(249,115,22,.35)' }}>
            + New Analysis
          </span>
          <div style={{ fontSize: 7, fontWeight: 700, letterSpacing: '.14em', color: 'rgba(255,255,255,.35)', margin: '0 4px 5px' }}>WORKSPACE</div>
          {[['Dashboard', true], ['History', false], ['Business Rules', false], ['Lifecycle', false]].map(([label, active]) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5.5px 8px', borderRadius: 7,
              background: active ? 'rgba(249,115,22,.16)' : 'transparent',
              color: active ? '#FDAF6D' : 'rgba(255,255,255,.6)', fontSize: 10,
              fontWeight: active ? 700 : 500, borderLeft: active ? '2px solid #F97316' : '2px solid transparent' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', opacity: .7 }} />{label}
            </span>
          ))}
          <div style={{ marginTop: 'auto' }}>
            <div style={{ fontSize: 7.5, color: 'rgba(255,255,255,.4)' }}>Total scans</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 7 }}>1115</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.06)',
              border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, padding: '5px 7px' }}>
              <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#3b4a63', color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, flex: 'none' }}>A</span>
              <span style={{ lineHeight: 1.2 }}>
                <span style={{ display: 'block', fontSize: 8.5, fontWeight: 700, color: '#fff' }}>admin</span>
                <span style={{ display: 'block', fontSize: 7, color: 'rgba(255,255,255,.45)' }}>admin@gmail.com</span>
              </span>
            </div>
          </div>
        </div>
        {/* main */}
        <div style={{ flex: 1, minWidth: 0, background: D.main, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* welcome card */}
          <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: 12,
            padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 8.5, fontWeight: 700,
                letterSpacing: '.1em', color: D.muted, marginBottom: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16A34A' }} />THURSDAY, JULY 30
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: D.text, letterSpacing: '-.02em' }}>
                You've analysed <span style={{ color: D.org }}>1115</span> drawings.
              </div>
              <div style={{ fontSize: 9.5, color: D.muted, marginTop: 3 }}>
                866 completed · 1758 critical characteristics · 23302 inspection items generated
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ display: 'flex', background: '#FAF7F2', border: `1px solid ${D.border}`, borderRadius: 10 }}>
                {[['2', 'TODAY'], ['78%', 'SUCCESS RATE']].map(([v, l], i) => (
                  <div key={l} style={{ padding: '8px 14px', textAlign: 'center',
                    borderLeft: i ? `1px solid ${D.border}` : 'none' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: D.org }}>{v}</div>
                    <div style={{ fontSize: 7, fontWeight: 700, letterSpacing: '.08em', color: D.muted }}>{l}</div>
                  </div>
                ))}
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: D.org, borderRadius: 9,
                padding: '8px 14px', color: '#fff', fontSize: 10.5, fontWeight: 700,
                boxShadow: '0 4px 14px rgba(249,115,22,.35)' }}>+ New Analysis</span>
            </div>
          </div>
          {/* stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
            {statCards.map(([cap, num, sub2, color, sp, icon]) => (
              <div key={cap} style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: 11, padding: '10px 11px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 7, display: 'inline-flex', alignItems: 'center',
                    justifyContent: 'center', color, background: `${color}18`, border: `1px solid ${color}35` }}>{icon}</span>
                  {spark(color, sp)}
                </div>
                <div style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '.09em', color: D.muted, marginTop: 8 }}>{cap}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color, letterSpacing: '-.02em' }}>{num}</div>
                <div style={{ fontSize: 8, color: D.muted }}>{sub2}</div>
              </div>
            ))}
          </div>
          {/* charts row */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1.9 1 300px', background: D.card, border: `1px solid ${D.border}`, borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: D.text }}>Activity Overview</div>
                  <div style={{ fontSize: 8.5, color: D.muted }}>Daily scan volume — last 7 days</div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, fontSize: 8.5, color: D.muted }}>
                  {[['Total', D.org], ['Success', '#16A34A'], ['Failed', '#EF4444']].map(([l, c]) => (
                    <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 2, background: c }} />{l}
                    </span>
                  ))}
                </div>
              </div>
              <svg viewBox="0 0 320 104" style={{ display: 'block', width: '100%', marginTop: 6 }}>
                <defs>
                  <linearGradient id="dmOrg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#F97316" stopOpacity="0.22"/><stop offset="1" stopColor="#F97316" stopOpacity="0"/>
                  </linearGradient>
                  <linearGradient id="dmGrn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#16A34A" stopOpacity="0.2"/><stop offset="1" stopColor="#16A34A" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                {[20, 42, 64, 86].map(y => <line key={y} x1="6" y1={y} x2="314" y2={y} stroke="#EDEAE4" strokeWidth="1" strokeDasharray="3 4"/>)}
                <path d="M8,88 C40,16 66,8 98,40 C128,70 158,88 190,86 C222,84 254,70 286,74 L312,78 L312,92 L8,92 Z" fill="url(#dmOrg)"/>
                <path d="M8,90 C40,32 66,24 98,52 C128,76 158,90 190,88 C222,86 254,76 286,79 L312,82 L312,92 L8,92 Z" fill="url(#dmGrn)"/>
                <path d="M8,88 C40,16 66,8 98,40 C128,70 158,88 190,86 C222,84 254,70 286,74 L312,78" fill="none" stroke="#F97316" strokeWidth="1.8"/>
                <path d="M8,90 C40,32 66,24 98,52 C128,76 158,90 190,88 C222,86 254,76 286,79 L312,82" fill="none" stroke="#16A34A" strokeWidth="1.6"/>
                <path d="M8,91 C40,84 66,82 98,88 C128,92 158,92.5 190,92 C222,91.5 254,89 286,90 L312,91" fill="none" stroke="#EF4444" strokeWidth="1.3"/>
                {['Jul 25', 'Jul 26', 'Jul 27', 'Jul 28', 'Jul 29', 'Jul 30'].map((d2, i) => (
                  <text key={d2} x={34 + i * 54} y="101" fontFamily="Inter,sans-serif" fontSize="7" fill="#A9AEB7" textAnchor="middle">{d2}</text>
                ))}
              </svg>
            </div>
            <div style={{ flex: '1 1 170px', background: D.card, border: `1px solid ${D.border}`, borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: D.text }}>Success Rate</div>
              <div style={{ fontSize: 8.5, color: D.muted, marginBottom: 6 }}>All-time distribution</div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <svg viewBox="0 0 42 42" style={{ width: 92, height: 92 }}>
                  <circle cx="21" cy="21" r="15.9" fill="none" stroke="#F1EEE8" strokeWidth="6"/>
                  <circle cx="21" cy="21" r="15.9" fill="none" stroke="#16A34A" strokeWidth="6"
                    strokeDasharray="78 22" strokeDashoffset="25" strokeLinecap="butt"/>
                  <circle cx="21" cy="21" r="15.9" fill="none" stroke="#EF4444" strokeWidth="6"
                    strokeDasharray="17 83" strokeDashoffset="-53" strokeLinecap="butt"/>
                  <circle cx="21" cy="21" r="15.9" fill="none" stroke="#F97316" strokeWidth="6"
                    strokeDasharray="5 95" strokeDashoffset="-70" strokeLinecap="butt"/>
                  <text x="21" y="20.5" fontFamily="Inter,sans-serif" fontSize="7.5" fontWeight="800" fill="#1F2430" textAnchor="middle">78%</text>
                  <text x="21" y="26.5" fontFamily="Inter,sans-serif" fontSize="3.4" fontWeight="700" fill="#8A8F98" textAnchor="middle" letterSpacing=".08em">SUCCESS</text>
                </svg>
              </div>
              {[['Successful', '866', '#16A34A'], ['Failed', '190', '#EF4444'], ['Pending', '59', '#F97316']].map(([l, v, c]) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3.5px 0', fontSize: 9.5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: c }} />
                  <span style={{ color: D.muted }}>{l}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: c }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroV3({ onGetStarted, scrollTo, onBookMeeting }) {
  const [approved, setApproved] = React.useState(true);
  return (
    <>
      <V3Css />
      <V3Nav onGetStarted={onGetStarted} scrollTo={scrollTo} onBookMeeting={onBookMeeting} />
      <section style={{ position: 'relative', overflow: 'hidden', background: V3.bg }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(1100px 520px at 72% -10%,rgba(255,122,0,.06),transparent 65%),radial-gradient(900px 500px at 8% 110%,rgba(60,90,130,.08),transparent 60%)' }} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(140,165,195,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(140,165,195,.04) 1px,transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(80% 80% at 50% 40%,#000 30%,transparent 100%)',
          WebkitMaskImage: 'radial-gradient(80% 80% at 50% 40%,#000 30%,transparent 100%)' }} />
        {/* vernier caliper — real Mitutoyo-style sketch (transparent PNG, inverted to blueprint lines) */}
        <img src={toolCaliper} alt="" aria-hidden="true"
          style={{ position: 'absolute', right: 'clamp(-40px,-1vw,10px)', top: -95,
            width: 'min(42%,580px)', opacity: .65, pointerEvents: 'none',
            transform: 'rotate(-10deg)',
            filter: 'invert(0.88) sepia(0.12) hue-rotate(180deg) saturate(1.4)' }} />
        {/* steel scale (rule) — technical line drawing in the same style as the caliper */}
        <svg viewBox="0 0 640 130" aria-hidden="true"
          style={{ position: 'absolute', right: 'clamp(-20px,1vw,40px)', bottom: 14,
            width: 'min(44%,600px)', opacity: .5, pointerEvents: 'none', transform: 'rotate(-9deg)' }}>
          <g stroke="rgba(201,218,236,.9)" fill="none" strokeWidth="1.3" strokeLinejoin="round">
            {/* rule body */}
            <rect x="10" y="30" width="608" height="62" rx="6"/>
            <line x1="10" y1="80" x2="618" y2="80" strokeWidth="0.7" opacity="0.6"/>
            {/* hanging hole */}
            <circle cx="34" cy="61" r="8"/>
            {/* mm graduations: 1mm short, 5mm medium, 10mm tall */}
            <g strokeWidth="0.85">
              {Array.from({ length: 111 }, (_, i) => 56 + i * 5).map((x, i) => {
                const h = i % 10 === 0 ? 22 : i % 5 === 0 ? 16 : 10;
                return <line key={x} x1={x} y1="30" x2={x} y2={30 + h}/>;
              })}
            </g>
            {/* cm numbers */}
            <g fontFamily="Inter,sans-serif" fontSize="12" fill="rgba(201,218,236,.9)" stroke="none">
              {Array.from({ length: 12 }, (_, i) => (
                <text key={i} x={56 + i * 50} y="70" textAnchor="middle">{i}</text>
              ))}
            </g>
            <g fontFamily="Inter,sans-serif" fontSize="10" fill="rgba(201,218,236,.85)" stroke="none">
              <text x="600" y="70" textAnchor="middle">cm</text>
              <text x="90" y="88" textAnchor="start">STAINLESS · HARDENED</text>
            </g>
          </g>
        </svg>
        <div style={{ position: 'relative', maxWidth: 1500, margin: '0 auto',
          padding: 'clamp(40px,6vh,72px) clamp(20px,3.5vw,48px) 72px',
          display: 'flex', flexWrap: 'wrap', gap: '52px clamp(24px,3vw,48px)', alignItems: 'center' }}>

          {/* left column */}
          <div className="lp3-fadeup" style={{ flex: '1.15 1 400px', minWidth: 'min(400px,100%)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500,
              color: V3.orgLight, border: '1px solid rgba(255,122,0,.4)', borderRadius: 999,
              padding: '6px 14px', marginBottom: 26, whiteSpace: 'nowrap' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: V3.org }} />
              AI-Powered Inspection Planning
            </span>
            <h1 style={{ fontWeight: 700, fontSize: 'clamp(38px,4.4vw,56px)', lineHeight: 1.22,
              letterSpacing: '-.012em', margin: '0 0 26px', color: '#f7f8fa' }}>
              From Engineering Drawing<br />to Inspection Plan.<br />
              <span style={{ background: 'linear-gradient(92deg,#FF7A00,#ffa14d)',
                WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>In Minutes.</span>
            </h1>
            <p style={{ fontSize: 16.5, lineHeight: 1.65, maxWidth: '44ch', margin: '0 0 30px', color: V3.body }}>
              Inspectly.AI reads CAD and manufacturing drawings, extracts critical dimensions,
              GD&amp;T, and tolerances, and generates accurate, ready-to-use inspection plans in seconds.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 38 }}>
              <button className="lp3-cta-primary" onClick={() => onBookMeeting('inspectly_hero')}>
                Book Meeting <V3ArrowSvg />
              </button>
              <button className="lp3-cta-ghost" onClick={() => scrollTo('how-it-works')}>
                <svg width="11" height="12" viewBox="0 0 11 12" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M1.5 1.5v9l8-4.5z"/></svg>
                Watch Demo
              </button>
            </div>
            <p style={{ margin: '-14px 0 32px', fontSize: 17, fontWeight: 700,
              letterSpacing: '.035em', color: '#e9edf2' }}>
              Scan it.<span style={{ color: '#FF7A00', margin: '0 10px' }}>◆</span>
              Check it.<span style={{ color: '#FF7A00', margin: '0 10px' }}>◆</span>
              Ship it.
            </p>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              {[
                [<svg key="c" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffa14d" strokeWidth="1.5" style={{ marginTop: 2, flex: 'none' }}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/></svg>, 'Save Hours', 'of Manual Work'],
                [<svg key="t" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffa14d" strokeWidth="1.5" style={{ marginTop: 2, flex: 'none' }}><circle cx="12" cy="12" r="7"/><path d="M12 1v5M12 18v5M1 12h5M18 12h5"/><circle cx="12" cy="12" r="1.6"/></svg>, 'Improve Accuracy', '& Consistency'],
                [<svg key="s" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffa14d" strokeWidth="1.5" style={{ marginTop: 2, flex: 'none' }}><path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5z"/><path d="M8.5 12l2.5 2.5 4.5-5"/></svg>, 'Ensure Compliance', 'Every Time'],
              ].map(([icon, t, s]) => (
                <span key={t} style={{ display: 'inline-flex', gap: 11, alignItems: 'flex-start' }}>
                  {icon}
                  <span style={{ fontSize: 14, lineHeight: 1.45 }}>
                    <strong style={{ display: 'block', fontWeight: 600, color: '#e9edf2' }}>{t}</strong>
                    <span style={{ color: V3.muted }}>{s}</span>
                  </span>
                </span>
              ))}
            </div>
          </div>

          {/* right column: app window mock (tilted like a laptop screen) */}
          <div className="lp3-fadeup-d lp3-tiltwrap" style={{ flex: '2.4 1 680px', minWidth: 'min(680px,100%)', position: 'relative' }}>
            <div className="lp3-tilt" style={{ position: 'relative' }}>
              <V3AppWindow approved={approved} setApproved={setApproved} />
              {/* floating summary card — inside the tilt, so it moves with the screen */}
              <div className="lp3-plan-card" style={{ position: 'absolute', right: -118, bottom: -34, width: 236, zIndex: 5 }}>
                <div className="lp3-plan-float" style={{ background: 'rgba(17,19,23,.97)', backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,.14)', borderRadius: 16, padding: '15px 16px 14px',
                  boxShadow: '0 28px 70px rgba(0,0,0,.6)' }}>
                  <div className="lp3-plan-row" style={{ display: 'flex', alignItems: 'center', gap: 9,
                    paddingBottom: 11, borderBottom: '1px solid rgba(255,255,255,.1)', animationDelay: '1.35s' }}>
                    <span style={{ width: 19, height: 19, borderRadius: '50%', background: 'rgba(63,214,140,.16)',
                      border: '1px solid rgba(63,214,140,.55)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#3fd68c" strokeWidth="2.6"><path d="M4 12.5l5 5L20 6.5"/></svg>
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', letterSpacing: '-.01em' }}>Inspection Plan Generated</span>
                  </div>
                  {[
                    ['Dimensions', '24', '1.5s'],
                    ['GD&T Symbols', '7', '1.62s'],
                    ['Notes', '3', '1.74s'],
                  ].map(([k, v, d]) => (
                    <div key={k} className="lp3-plan-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 2px', borderBottom: '1px solid rgba(255,255,255,.08)', animationDelay: d }}>
                      <span style={{ fontSize: 12, color: '#C3CBD6' }}>{k}</span>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: '#ffffff', fontFeatureSettings: "'tnum' 1" }}>{v}</span>
                    </div>
                  ))}
                  <div className="lp3-plan-row lp3-togglerow" onClick={() => setApproved(a => !a)}
                    title="Click to toggle approval status"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 2px', borderBottom: '1px solid rgba(255,255,255,.08)', animationDelay: '1.86s' }}>
                    <span style={{ fontSize: 12, color: approved ? '#C3CBD6' : '#f4a0a0', transition: 'color .35s ease' }}>
                      Approval Status{approved ? '' : ' · Pending'}
                    </span>
                    <span style={{ width: 27, height: 15, borderRadius: 999, flex: 'none', position: 'relative',
                      background: approved ? '#22C55E' : '#f05252',
                      boxShadow: approved ? '0 0 10px rgba(34,197,94,.45)' : '0 0 10px rgba(240,82,82,.4)',
                      transition: 'background .35s ease, box-shadow .35s ease' }}>
                      <span style={{ position: 'absolute', top: 2, left: approved ? 14 : 2, width: 11, height: 11,
                        borderRadius: '50%', background: '#fff', transition: 'left .35s cubic-bezier(.16,1,.3,1)' }} />
                    </span>
                  </div>
                  <button className="lp3-plan-btn lp3-plan-row" style={{ animationDelay: '2s' }} onClick={() => onGetStarted('uploader')}>
                    View Plan
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12M7 10l5 5 5-5M4 21h16"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Main component                                                       */
/* ─────────────────────────────────────────────────────────────────── */
export default function LandingPage({ onGetStarted }) {
  const [scrolled,       setScrolled]       = useState(false);
  const [isLightSection, setIsLightSection] = useState(false);

  // ── hero parallax refs ──────────────────────────────────────────
  const heroRef = useRef(null);

  // ── scroll-reveal refs ─────────────────────────────────────────
  const hiwRef      = useScrollReveal();
  const featuresRef = useScrollReveal();
  const previewRef  = useScrollReveal();

  // ── restore body scroll (app shell locks it) ────────────────────
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const ph = html.style.overflow;
    const pb = body.style.overflow;
    html.style.overflow = 'auto';
    body.style.overflow = 'auto';
    return () => { html.style.overflow = ph; body.style.overflow = pb; };
  }, []);

  // ── navbar scroll state + light section detection ───────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 1);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const LIGHT_IDS = ['how-it-works', 'features', 'product-preview', 'contact'];
    const activeSet = new Set();
    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) activeSet.add(e.target.id);
          else activeSet.delete(e.target.id);
        });
        setIsLightSection(activeSet.size > 0);
      },
      { rootMargin: '-64px 0px -80% 0px', threshold: 0 }
    );
    LIGHT_IDS.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => { window.removeEventListener('scroll', onScroll); obs.disconnect(); };
  }, []);

  // ── mouse parallax on hero ──────────────────────────────────────
  const handleMouseMove = useCallback((e) => {
    const el = heroRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width  / 2;
    const cy = rect.top  + rect.height / 2;
    const dx = (e.clientX - cx) / rect.width  * -18;
    const dy = (e.clientY - cy) / rect.height * -14;
    el.querySelectorAll('.lp-parallax').forEach(node => {
      node.style.transform = `translate(${dx}px, ${dy}px)`;
    });
  }, []);
  const handleMouseLeave = useCallback(() => {
    heroRef.current?.querySelectorAll('.lp-parallax').forEach(node => {
      node.style.transform = 'translate(0,0)';
    });
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  /* Book Meeting lead modal — value is the per-surface `product` string */
  const [leadProduct, setLeadProduct] = useState(null);

  /* nav appearance */
  const navMode = isLightSection ? 'light' : scrolled ? 'dark' : 'hero';
  const textCol     = navMode === 'light' ? '#1A1410'             : '#FFFFFF';
  const linkCol     = navMode === 'light' ? 'rgba(26,20,16,0.7)'  : 'rgba(255,255,255,0.65)';

  return (
    <div className="lp-root">

      {leadProduct && (
        <BookMeetingModal product={leadProduct} onClose={() => setLeadProduct(null)} />
      )}

      {/* ── Navbar + Hero — design_handoff v3 replica ───────────────── */}
      <HeroV3 onGetStarted={onGetStarted} scrollTo={scrollTo} onBookMeeting={setLeadProduct} />

      {/* ── Stats bar ───────────────────────────────────────────────── */}
      <StatsBar />

      {/* ── How It Works ────────────────────────────────────────────── */}
      <section id="how-it-works" className="lp-section" ref={hiwRef}>
        <div className="lp-section-inner">
          <div className="lp-eyebrow lp-reveal">How It Works</div>
          <h2 className="lp-section-h2 lp-reveal">
            Your next drawing could be an inspection plan in{' '}
            <span style={{ color: '#F97316' }}>5 minutes</span>
          </h2>
          <p className="lp-section-sub lp-reveal">
            From drawing to inspection plan in 4 steps. No manual data entry. No formatting. Just upload.
          </p>
          <div className="lp-steps-grid">
            {STEPS.map(step => (
              <div key={step.num} className="lp-step-card lp-reveal">
                <div className="lp-step-num">{step.num}</div>
                <div className="lp-step-icon">{step.icon}</div>
                <h3 className="lp-step-title">{step.title}</h3>
                <p className="lp-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────── */}
      <section id="features" className="lp-section lp-section-light" ref={featuresRef}>
        <div className="lp-section-inner">
          <div className="lp-eyebrow lp-reveal">Features</div>
          <h2 className="lp-section-h2 lp-reveal">Everything Your QA Team Needs</h2>
          <p className="lp-section-sub lp-reveal">
            Purpose-built for manufacturing inspection workflows — from first scan to final approval.
          </p>
          <div className="lp-features-grid">
            {FEATURES.map(f => (
              <div key={f.title} className="lp-feature-card lp-reveal">
                <div className="lp-feature-icon"
                  style={{ color: f.accent, background: f.accent + '15', borderColor: f.accent + '30' }}>
                  {f.icon}
                </div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Product Preview ──────────────────────────────────────────── */}
      <section id="product-preview" className="lp-section lp-section-white" ref={previewRef}>
        <div className="lp-section-inner">
          <div className="lp-eyebrow lp-reveal">Product Preview</div>
          <h2 className="lp-section-h2 lp-reveal">A Dashboard Built for Precision</h2>
          <p className="lp-section-sub lp-reveal">
            Track every drawing, every scan, and every approval — all in one place.
          </p>

          <div className="lp-reveal">
            <DashboardMock />
          </div>
        </div>
      </section>

      {/* ── Contact ─────────────────────────────────────────────────── */}
      <section id="contact" className="lp-section lp-section-light">
        <div className="lp-section-inner">
          <div className="lp-eyebrow">Contact Us</div>
          <h2 className="lp-section-h2">Book a Meeting With Our Team</h2>
          <p className="lp-section-sub">
            Have questions or want a walkthrough of InSpectly.AI? Send us a message
            and we'll get back to you shortly.
          </p>
          <div className="lp-enquiry-wrap">
            <EnquiryForm product="inspectly_contact" />
            <p className="lp-enquiry-testing-note">Feedback form still in testing</p>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-logo">
            <div className="lp-nav-logo-icon"><IconLogo /></div>
            <span className="lp-nav-logo-text">
              InSpectly<span style={{ color: '#F97316' }}>.AI</span>
            </span>
          </div>
          <p className="lp-footer-tagline">
            AI-powered inspection plan generation for manufacturing quality teams.
          </p>
          <p className="lp-footer-copy">© 2026 InSpectly.AI · All rights reserved</p>
        </div>
      </footer>

    </div>
  );
}
